import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { logEvent } from "@shared/analytics/analytics";
import { PageHeader } from "@shared/components/PageHeader";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Card, CardContent } from "@shared/ui/card";
import { Info } from "lucide-react";
import React, { useEffect } from "react";

interface AssetDetailsTabProps {
  data: any;
}

export const AssetDetailsTab: React.FC<AssetDetailsTabProps> = ({ data }) => {
  useEffect(() => {
    logEvent("asset_details_tab_viewed");
  }, []);

  const currency = useCurrency();
  const sections = [
    {
      title: "Classification",
      description: "Asset categorization and facility location details",
      fields: {
        "Asset Class:": data?.assetClassDisplay ?? "NA",
        "Asset Type:": data?.assetTypeDisplay ?? "NA",
        "Facility:": data?.facilityName ?? "NA",
        "Location:": data?.location ?? "NA",
      },
    },
    {
      title: "Technical Details",
      description: "Manufacturing specifications and technical characteristics",
      fields: {
        "Serial Number:": data?.serialNumber ?? "NA",
        "Manufacturer:": data?.manufacturerName ?? "NA",
        "Useful Life:": data?.usefulLife ?? "NA",
        "Parent Class:": data?.parentAssetName ?? "NA",
        "Child Asset:": data?.childAssetName ?? "NA",
      },
    },
    {
      title: "Financial Information",
      description:
        "Cost tracking and depreciation values for financial planning",
      fields: {
        "Installation Cost:": data?.installationCost
          ? `${currency} ${data.installationCost}`
          : "NA",
        "Replacement Cost:": data?.replacementCost
          ? `${currency} ${data.replacementCost}`
          : "NA",
        "Current Value:": data?.currentValue ?? "NA",
        "Depreciation Method:": data?.depreciationMethodDisplay ?? "NA",
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Asset Details */}
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
          <Info className="h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <PageHeader
            title="Asset Details"
            subtitle="Basic classification and location information for this asset"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sections.map((section) => (
          <Card key={section.title}>
            <h4 className="font-medium text-gray-900 mb-1">{section.title}</h4>
            <p className="text-xs text-gray-500 mb-3">{section.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4 mt-2">
              {Object.entries(section.fields).map(([field, value]) => (
                <div key={field}>
                  {/* <span className="text-gray-600">{field} </span>
                      <span className="font-medium ml-2">{value}</span> */}
                  <ValueLabel
                    label={field}
                    value={value}
                    // layout="horizontal"
                  />
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Asset Specific Parameters */}
      <Card>
        <PageHeader
          title="Asset-Specific Parameters"
          subtitle="Unique technical specifications and operational parameters for this asset type"
        />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data?.dimensions && Object.keys(data.dimensions).length > 0 ? (
              Object.entries(data.dimensions).map(([key, value]) => (
                <div key={key} className="p-4 border rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-1">{key}</h4>
                  <p className="text-lg font-semibold text-blue-600">
                    {typeof value === "boolean"
                      ? value
                        ? "Yes"
                        : "No"
                      : String(value)}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-gray-500 col-span-full">
                No dimensions available
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
